import { useState, useEffect } from 'react'

export type RelativeTimeFormat = 'short' | 'long' | 'full'

export interface RelativeTimeProps {
  date: string | Date | null | undefined
  format?: RelativeTimeFormat
  updateInterval?: number // in milliseconds, 0 to disable auto-update
  className?: string
}

// Helper function to format date with time
const formatDateWithTime = (date: Date, showYear: boolean): string => {
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: showYear ? 'numeric' : undefined,
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Helper function to format date string
const formatDateString = (date: Date): string => {
  return date.toLocaleString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Helper function to calculate time differences
const calculateTimeDiffs = (diffMs: number) => {
  return {
    diffMins: Math.floor(diffMs / 60000),
    diffHours: Math.floor(diffMs / 3600000),
    diffDays: Math.floor(diffMs / 86400000),
    diffWeeks: Math.floor(Math.floor(diffMs / 86400000) / 7),
    diffMonths: Math.floor(Math.floor(diffMs / 86400000) / 30),
    diffYears: Math.floor(Math.floor(diffMs / 86400000) / 365)
  }
}

// Helper function to format short time
const formatShortTime = (diffMins: number, diffHours: number, diffDays: number): string | null => {
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return null
}

// Helper function to format long time
const formatLongTime = (diffWeeks: number, diffMonths: number, diffYears: number): string => {
  if (diffWeeks < 4) return `${diffWeeks}w ago`
  if (diffMonths < 12) return `${diffMonths}mo ago`
  return `${diffYears}y ago`
}

// Helper function to handle future dates
const handleFutureDate = (date: Date, _now: Date, format: RelativeTimeFormat): string => {
  return format === 'full' ? formatDateString(date) : 'Just now'
}

// Helper function to format for full format with older dates
const formatFullFormatOldDate = (date: Date, now: Date): string => {
  return formatDateWithTime(date, date.getFullYear() !== now.getFullYear())
}

// Helper function to format based on format type
const formatByType = (diffMins: number, diffHours: number, diffDays: number, diffWeeks: number, diffMonths: number, diffYears: number, date: Date, now: Date, format: 'short' | 'long' | 'full'): string => {
  // Try short format first
  const shortFormat = formatShortTime(diffMins, diffHours, diffDays)
  if (shortFormat) return shortFormat
  
  // Long format for weeks/months/years
  if (format === 'long' || format === 'full') {
    return formatLongTime(diffWeeks, diffMonths, diffYears)
  }
  
  // For short format with older dates, show formatted date
  return formatDateWithTime(date, date.getFullYear() !== now.getFullYear())
}

// Main function to format relative time
const formatRelativeTime = (dateStr: string | Date | null | undefined, format: RelativeTimeFormat): string => {
  if (!dateStr) return '—'
  
  try {
    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    
    // Handle future dates
    if (diffMs < 0) {
      return handleFutureDate(date, now, format)
    }

    const { diffMins, diffHours, diffDays, diffWeeks, diffMonths, diffYears } = calculateTimeDiffs(diffMs)

    // Handle full format with older dates
    if (format === 'full' && diffDays >= 7) {
      return formatFullFormatOldDate(date, now)
    }

    return formatByType(diffMins, diffHours, diffDays, diffWeeks, diffMonths, diffYears, date, now, format)
  } catch (e) {
    return '—'
  }
}

function RelativeTime({ 
  date, 
  format = 'short',
  updateInterval = 60000, // Update every minute by default
  className = ''
}: RelativeTimeProps) {
  const [displayText, setDisplayText] = useState<string>('—')

  useEffect(() => {
    // Initial format
    setDisplayText(formatRelativeTime(date, format))

    // Set up auto-update if interval is provided and > 0
    if (updateInterval > 0 && date) {
      const interval = setInterval(() => {
        setDisplayText(formatRelativeTime(date, format))
      }, updateInterval)

      return () => clearInterval(interval)
    }
  }, [date, format, updateInterval])

  return (
    <span className={className}>
      {displayText}
    </span>
  )
}

export default RelativeTime
